import React from 'react';
import { computePlanHash } from '@akem/shared';
import { useLocation, useNavigate } from 'react-router-dom';
import { getProfileById, searchOrCreateProfile } from '../../services/profile.js';
import { CanvasLayout } from '../canvas/index.js';
import { useCanvasStore } from '../canvas/stores/canvasStore.js';
import { orchestratorAgent } from '../canvas/services/orchestrator.js';
import {
  buildProfileCanvasPlan,
  PROFILE_CANVAS_PLAN_VERSION,
} from '../canvas/services/profileCanvasPlan.js';
import { ensureProfileDashboardCanvas } from '../canvas/services/profileDashboardReconciler.js';
import type { CanvasState as StoreCanvasState } from '../canvas/types/index.js';

function useQuery() {
  const { search } = useLocation();
  return React.useMemo(() => new URLSearchParams(search), [search]);
}

const MIN_SECOND_ORDER_WIDGETS = 6;
const PROFILE_CANVAS_VERSION = PROFILE_CANVAS_PLAN_VERSION;

const profileCanvasKey = (profileId: string) => `profile:${profileId}`;

const getCanvasVersion = (canvas: StoreCanvasState | undefined) =>
  canvas?.metadata?.profileCanvasVersion as string | undefined;

const hasProfileCanvasV2 = (
  canvas: StoreCanvasState | undefined,
  profileId: string
): boolean => {
  if (!canvas) return false;
  if (canvas.key !== profileCanvasKey(profileId)) return false;
  const version = getCanvasVersion(canvas);
  if (version !== PROFILE_CANVAS_VERSION) return false;
  const metadataProfileId = canvas.metadata?.profileId as string | undefined;
  if (metadataProfileId && metadataProfileId !== profileId) return false;
  const headerPresent = canvas.widgets.some(
    (w) =>
      w.type === 'profileHeader' &&
      ((w.config as any)?.profileId === profileId ||
        (w.inputs || []).some(
          (input) =>
            input?.value && typeof input.value === 'object' && input.value?.id === profileId
        ))
  );
  if (!headerPresent) return false;
  const summaryCount = canvas.widgets.filter(
    (w) =>
      w.type === 'akemIntelligence' && (w.config as any)?.profileId === profileId
  ).length;
  return summaryCount >= MIN_SECOND_ORDER_WIDGETS;
};

export const CanvasRouteHandler: React.FC = () => {
  const query = useQuery();
  const navigate = useNavigate();
  const queryKey = React.useMemo(() => query.toString(), [query]);

  // Enforce profile canvas intent: if no profile parameter is provided, redirect to Market
  React.useEffect(() => {
    const tickerParam = query.get('ticker');
    const profileIdParam = query.get('profileId');
    if (!tickerParam && !profileIdParam) {
      navigate('/market', { replace: true });
    }
  }, [queryKey, navigate]);

  // Guard against duplicate applyCanvasPlan calls during rapid route changes
  const lastAppliedRef = React.useRef<{ profileId: string; planHash: string; ts: number } | null>(null);

  React.useEffect(() => {

    let cancelled = false;
    const tickerParam = query.get('ticker');
    const profileIdParam = query.get('profileId');

    const resolveProfileId = async (): Promise<string | null> => {
      let id = profileIdParam;
      if (!id && tickerParam) {
        try {
          const res = await searchOrCreateProfile(tickerParam);
          if (!cancelled && res?.profile && (res.profile as any).id) {
            id = String((res.profile as any).id);
            navigate(`/canvas?profileId=${encodeURIComponent(id)}`, {
              replace: true,
            });
          }
        } catch (err) {
          if (!cancelled) {
            console.warn('[CanvasRouteHandler] searchOrCreateProfile failed', err);
          }
        }
      }
      return id || null;
    };

    (async () => {
      const resolvedProfileId = await resolveProfileId();
      if (!resolvedProfileId || cancelled) return;

      let profileName: string | undefined;
      let resolvedTicker = tickerParam || undefined;
      try {
        const profileRes = await getProfileById(resolvedProfileId);
        if (!cancelled && profileRes?.success && profileRes.profile) {
          profileName = profileRes.profile.name;
          resolvedTicker = profileRes.profile.ticker || resolvedTicker;
        }
      } catch (err) {
        if (!cancelled) {
          console.warn('[CanvasRouteHandler] getProfileById failed', err);
        }
      }

      let storeState = useCanvasStore.getState();
      let canvas = storeState.canvases.find(
        (c) => c.key === profileCanvasKey(resolvedProfileId)
      );
      let canvasId = canvas?.id;

      if (!canvasId) {
        const displayName = profileName && profileName.trim().length > 0 ? profileName : resolvedProfileId;
        canvasId = storeState.createCanvas(`Profile · ${displayName}`);
        storeState = useCanvasStore.getState();
        canvas = storeState.canvases.find((c) => c.id === canvasId);
      } else if (storeState.activeCanvasId !== canvasId) {
        storeState.setActiveCanvas(canvasId);
        storeState = useCanvasStore.getState();
        canvas = storeState.canvases.find((c) => c.id === canvasId);
      }

      if (!canvasId || cancelled) return;

      const displayName = profileName && profileName.trim().length > 0 ? profileName : resolvedProfileId;
      storeState.setCanvasIdentity(canvasId, {
        key: profileCanvasKey(resolvedProfileId),
        kind: 'profile',
        name: `Profile · ${displayName}`,
      });
      storeState = useCanvasStore.getState();
      canvas = storeState.canvases.find((c) => c.id === canvasId);

      const planArgs = buildProfileCanvasPlan({
        profileId: resolvedProfileId,
        profileName,
        ticker: resolvedTicker,
      });
      let computedPlanHash: string | undefined;
      try {
        computedPlanHash = computePlanHash(planArgs);
      } catch {}

      if (!hasProfileCanvasV2(canvas, resolvedProfileId)) {
        storeState.zoomOut(canvasId);
        storeState = useCanvasStore.getState();
        storeState.clearCanvas(canvasId);
        // Idempotency guard: avoid re-applying the identical plan within a short window
        try {
          const now = Date.now();
          const last = lastAppliedRef.current;
          if (
            last &&
            last.profileId === resolvedProfileId &&
            computedPlanHash &&
            last.planHash === computedPlanHash &&
            now - last.ts < 10000 // 10s window to coalesce duplicate applies
          ) {
            return;
          }
          if (computedPlanHash) {
            lastAppliedRef.current = {
              profileId: resolvedProfileId,
              planHash: computedPlanHash,
              ts: now,
            };
          } else {
            lastAppliedRef.current = { profileId: resolvedProfileId, planHash: 'unknown', ts: now };
          }
        } catch {}
        try {
          await orchestratorAgent.executeToolCall({
            id: `apply-profile-${resolvedProfileId}-${Date.now()}`,
            toolName: 'applyCanvasPlan',
            args: planArgs,
          });
          if (!cancelled) {
            useCanvasStore
              .getState()
              .updateViewport(canvasId, { x: 0, y: 0, zoom: 1 });
            // Strict reconciliation to remove any stale widgets
            ensureProfileDashboardCanvas(canvasId, {
              profileId: resolvedProfileId,
              profileName,
              ticker: resolvedTicker,
            });
            const metadata: Record<string, any> = {
              profileCanvasVersion: PROFILE_CANVAS_VERSION,
              profileId: resolvedProfileId,
            };
            if (computedPlanHash) metadata.profileCanvasPlanHash = computedPlanHash;
            if (resolvedTicker) metadata.profileTicker = resolvedTicker;
            useCanvasStore.getState().setCanvasMetadata(canvasId, metadata);
          }
        } catch (err) {
          if (!cancelled) {
            console.warn('[CanvasRouteHandler] Failed to apply profile canvas plan', err);
          }
        }
      } else {
        // Even if it looks like v2, reconcile to remove any extras or mismatched widgets
        try {
          ensureProfileDashboardCanvas(canvasId, {
            profileId: resolvedProfileId,
            profileName,
            ticker: resolvedTicker,
          });
          const metadata: Record<string, any> = {
            profileCanvasVersion: PROFILE_CANVAS_VERSION,
            profileId: resolvedProfileId,
          };
          if (computedPlanHash) metadata.profileCanvasPlanHash = computedPlanHash;
          if (resolvedTicker) metadata.profileTicker = resolvedTicker;
          useCanvasStore.getState().setCanvasMetadata(canvasId, metadata);
        } catch (e) {
          console.warn('[CanvasRouteHandler] Failed to reconcile profile dashboard', e);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [queryKey, navigate]);

  // Hide the widget palette for the v2 profile dashboard; keep canvas interactive
  // Profile dashboard is read-only and has no widget palette.
  return <CanvasLayout hidePalette paletteInitiallyOpen={false} readOnly />;
};
